<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-report-stock-usage">
        <i class="far fa-ballot"></i> Stock Usage / Incoming Report
      </Tab>
      <Tab tabid="tab-report-purchasing">
        <i class="far fa-clipboard-list"></i> Stock Purchasing Report
      </Tab>
      <Tab tabid="tab-report-container-purchasing">
        <i class="far fa-container-storage"></i> Container Purchasing
      </Tab>
      <Tab tabid="tab-report-supplier-performance">
        <i class="far fa-chart-line"></i> Supplier Performance Report
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-report-stock-usage">
        <div class="row no-gutter">
          <div class="col-lg-12">
            <DataTable ref="stockReorderTable" :columns="columnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="0" :url="reorderReportDataURL" :displayExportTools="true" exportTitle="Stock Reorder Report" :displayTools="true">
              <template v-slot:title>
                <ContentTitle>Stock Usage Report</ContentTitle>
                <ContentSubTitle>Showing incoming stock from {{appliedStockReorderFilter.incomingStockStartDate | formatDateCustom('MMM YYYY')}} to {{appliedStockReorderFilter.incomingStockEndDate | formatDateCustom('MMM YYYY')}}</ContentSubTitle>
                <ContentSubTitle>Showing stock usage from {{appliedStockReorderFilter.stockUsageStartDate | formatDateCustom('MMM YYYY')}} to {{appliedStockReorderFilter.stockUsageEndDate | formatDateCustom('MMM YYYY')}}</ContentSubTitle>
              </template>
              <template v-slot:tableHeader>
                <DataTableTopHeaderRow>
                  <DataTableHeaderCell colspan="6"></DataTableHeaderCell>
                  <DataTableHeaderCell :colspan="incomingStockMonthList.length">Incoming Stock</DataTableHeaderCell>
                </DataTableTopHeaderRow>
              </template>
              <template v-slot:tools>
                <ReportFilterPopover
                  class="float-right"
                  headerTitle="Stock Reorder Filters"
                  sizeClass="popover-xxlarge"
                  :showApplyButton="true"
                  :showResetButton="true"
                  @apply="applyStockReorderFilter"
                  @reset="resetStockReorderFilter"
                >
                  <template v-slot:popover>
                    <div class="row no-gutter">
                      <div class="col-6">
                        <ReportFilterGroup label="Incoming Stock Date Range" subTitle="Date range when purchase order is expected to arrive">
                          <ReportDateRangeSelect 
                            :startDate="editingStockReorderFilter.incomingStockStartDate"
                            :endDate="editingStockReorderFilter.incomingStockEndDate"
                            @updateStartDate="editingStockReorderFilter.incomingStockStartDate = $event"
                            @updateEndDate="editingStockReorderFilter.incomingStockEndDate = $event"
                          />
                        </ReportFilterGroup>
                      </div>
                      <div class="col-6">
                        <ReportFilterGroup label="Stock Usage Date Range" subTitle="Date range for stock usage data">
                          <ReportDateRangeSelect 
                            :startDate="editingStockReorderFilter.stockUsageStartDate"
                            :endDate="editingStockReorderFilter.stockUsageEndDate"
                            @updateStartDate="editingStockReorderFilter.stockUsageStartDate = $event"
                            @updateEndDate="editingStockReorderFilter.stockUsageEndDate = $event"
                          />
                        </ReportFilterGroup>
                      </div>
                    </div>
                    <FormHorizontalRule />
                    <ReportFilterGroup label="Purchase Order Status Filter">
                      <ReportMultiSelect :options="PurchaseOrderStatusTypeLookup" v-model="editingStockReorderFilter.poStatusList" />
                    </ReportFilterGroup>

                    <FormHorizontalRule />
                    <ReportFilterGroup label="Other Filters">
                      <div class="row no-gutter mb-2">
                        <div class="col-6">
                          Hide Purchasing Not Required
                          <FormSwitch booleanMode v-model="editingStockReorderFilter.hidePurchasingNotRequired" />
                        </div>
                        <div class="col-6">
                          Hide Purchasing Paused
                          <FormSwitch booleanMode v-model="editingStockReorderFilter.hidePurchasingPaused" />
                        </div>
                      </div>
                    </ReportFilterGroup>
                  </template>
                  <template v-slot:filterDisplay>
                    <div>
                      <Tooltip label="Incoming stock date range">
                        <i class="far fa-arrow-alt-to-bottom mr-2"></i>{{appliedStockReorderFilter.incomingStockStartDate | formatDateCustom('MMM YYYY')}} - {{appliedStockReorderFilter.incomingStockEndDate | formatDateCustom('MMM YYYY')}}
                      </Tooltip>
                    </div>
                    <div>
                      <Tooltip label="Stock usage date range">
                        <i class="far fa-arrow-alt-from-bottom mr-2"></i>{{appliedStockReorderFilter.stockUsageStartDate | formatDateCustom('MMM YYYY')}} - {{appliedStockReorderFilter.stockUsageEndDate | formatDateCustom('MMM YYYY')}}
                      </Tooltip>
                    </div>
                  </template>
                </ReportFilterPopover>
              </template>
              <template v-slot:row="row">
                <tr :key="row.data.id">
                  <td class="table-data-nav-link">
                    <LinkStock :id="row.data.stockId" :name="row.data.stockName" />
                  </td>
                  <td>
                    {{row.data.outputSize}}
                  </td>
                  <td>
                    <IconBoolean :value="!!row.data.isPurchasingRequired" />
                  </td>
                  <td>
                    <IconBoolean :value="!!row.data.isPurchasingPaused" v-if="!!row.data.isPurchasingPaused" />
                    <span v-if="!row.data.isPurchasingPaused">-</span>
                  </td>
                  <td>
                    {{row.data.onHandTotalSheets}}
                  </td>
                  <td>
                    {{row.data.stockUsageTotalSheets}}
                  </td>
                  <td>
                    {{row.data.stockManualTotalSheets}}
                  </td>
                  <td>
                    {{row.data.averageStockUsage}}
                  </td>
                  <template v-for="(monthInfo, index) of incomingStockMonthList">
                    <td :key="index" class="table-data-alternate-column">
                      {{row.data[monthInfo.data]}}
                    </td>
                  </template>
                  <td>
                    {{row.data.futureEstStockUsage}}
                  </td>
                  <td :class="{'text-center': true, 'financial-loss': row.data.surplusSheets < 0, 'financial-profit': row.data.surplusSheets > 0}">
                    {{row.data.surplusSheets}}
                  </td>
                  <td>
                    {{row.data.blockSize}}
                  </td>
                  <td :class="{'text-center': true, 'financial-loss': row.data.surplusBlocks < 0, 'financial-profit': row.data.surplusBlocks > 0}">
                    {{row.data.surplusBlocks}}
                  </td>
                </tr>
              </template>
            </DataTable>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-report-purchasing">
        <div class="row no-gutter">
          <div class="col-lg-12">
            <ReportFilterPopover
              class="float-right"
              headerTitle="Stock Purchase Filters"
              sizeClass="popover-xxlarge"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applyStockPurchaseFilter"
              @reset="resetStockPurchaseFilter"
            >
              <template v-slot:popover>
                <div class="row no-gutter">
                  <div class="col-6">
                    <ReportFilterGroup label="Incoming Stock Date Range" subTitle="Date range when purchase order is expected to arrive">
                      <ReportDateRangeSelect 
                        :startDate="editingStockPurchaseFilter.incomingStockStartDate"
                        :endDate="editingStockPurchaseFilter.incomingStockEndDate"
                        @updateStartDate="editingStockPurchaseFilter.incomingStockStartDate = $event"
                        @updateEndDate="editingStockPurchaseFilter.incomingStockEndDate = $event"
                      />
                    </ReportFilterGroup>
                  </div>
                  <div class="col-6">
                    <ReportFilterGroup label="Stock Usage Date Range" subTitle="Date range for stock usage data">
                      <ReportDateRangeSelect 
                        :startDate="editingStockPurchaseFilter.stockUsageStartDate"
                        :endDate="editingStockPurchaseFilter.stockUsageEndDate"
                        @updateStartDate="editingStockPurchaseFilter.stockUsageStartDate = $event"
                        @updateEndDate="editingStockPurchaseFilter.stockUsageEndDate = $event"
                      />
                    </ReportFilterGroup>
                  </div>
                </div>
                <FormHorizontalRule />
                <ReportFilterGroup label="Purchase Order Status Filter">
                  <ReportMultiSelect :options="PurchaseOrderStatusTypeLookup" v-model="editingStockPurchaseFilter.poStatusList" />
                </ReportFilterGroup>
                <FormHorizontalRule />
                
                <ReportFilterGroup label="Other Filters">
                  <div class="row no-gutter mb-2">
                    <div class="col-6">
                      <Tooltip label="Hides glass sizes with no daily usage" helpStyle>Hide Unused Stock</Tooltip>
                      <FormSwitch booleanMode v-model="editingStockPurchaseFilter.hideUnusedStock" />
                    </div>
                    <div class="col-3">
                      <Tooltip label="Hides glass sizes with sufficient stock levels that don't require purchasing" helpStyle>Hide Sufficient Stock Items</Tooltip>
                      <FormSwitch booleanMode v-model="editingStockPurchaseFilter.hideSufficientStock" />
                    </div>
                    <div class="col-3">
                      <Tooltip label="Hides glass sizes have purchasing disabled or are paused" helpStyle>Hide Purchasing Not Required</Tooltip>
                      <FormSwitch booleanMode v-model="editingStockPurchaseFilter.hidePurchasingNotRequired" />
                    </div>
                  </div>
                  <div class="row no-gutter mb-2">
                    <div class="col-6">
                      Minimum Days Until Purchase
                      <FormInputText type="number" v-model="editingStockPurchaseFilter.minDaysUntilPurchase" note="Minimum number of days until a purchase is required" />
                    </div>
                    <div class="col-6">
                      Minimum Shortfall Days
                      <FormInputText type="number" v-model="editingStockPurchaseFilter.minShortfallDays" note="Minimum number of days required with the stock below required level before flagging purchase required" />
                    </div>
                  </div>
                  <div class="row no-gutter mb-2">
                    <div class="col-6">
                      Minimum Purchase Spacing Days
                      <FormInputText type="number" v-model="editingStockPurchaseFilter.minPurchaseSpacingDays" note="Minimum number of business days required between purchases for the same item. Decrease for more frequent purchases" />
                    </div>
                  </div>
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <Tooltip label="Incoming stock date range">
                    <i class="far fa-arrow-alt-to-bottom mr-2"></i>{{appliedStockPurchaseFilter.incomingStockStartDate | formatDateCustom('MMM YYYY')}} - {{appliedStockPurchaseFilter.incomingStockEndDate | formatDateCustom('MMM YYYY')}}
                  </Tooltip>
                </div>
                <div>
                  <Tooltip label="Stock usage date range">
                    <i class="far fa-arrow-alt-from-bottom mr-2"></i>{{appliedStockPurchaseFilter.stockUsageStartDate | formatDateCustom('MMM YYYY')}} - {{appliedStockPurchaseFilter.stockUsageEndDate | formatDateCustom('MMM YYYY')}}
                  </Tooltip>
                </div>
              </template>
            </ReportFilterPopover>

            <ContentTitle>Stock Purchasing Report</ContentTitle>
            <ContentSubTitle>Showing incoming stock from {{appliedStockPurchaseFilter.incomingStockStartDate | formatDateCustom('MMM YYYY')}} to {{appliedStockPurchaseFilter.incomingStockEndDate | formatDateCustom('MMM YYYY')}}</ContentSubTitle>
            <ContentSubTitle>Showing stock usage from {{appliedStockPurchaseFilter.stockUsageStartDate | formatDateCustom('MMM YYYY')}} to {{appliedStockPurchaseFilter.stockUsageEndDate | formatDateCustom('MMM YYYY')}}</ContentSubTitle>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12">

            <Spinner v-if="loadingPurchaseReport" />
            <PurchaseOrderReorderTable
              v-if="!loadingPurchaseReport"
              :usageStartDate="appliedStockPurchaseFilter.stockUsageStartDate"
              :usageEndDate="appliedStockPurchaseFilter.stockUsageEndDate"
              :incomingStartDate="appliedStockPurchaseFilter.incomingStockStartDate"
              :incomingEndDate="appliedStockPurchaseFilter.incomingStockEndDate"
              :stockPurchasingList="stockPurchasingList" 
            />
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-report-container-purchasing">
        <div class="row no-gutter">
          <div class="col-lg-12">
            <ReportFilterPopover
              class="float-right"
              headerTitle="Container Purchase Filters"
              sizeClass="popover-xxlarge"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applyContainerPurchaseFilter"
              @reset="resetContainerPurchaseFilter"
            >
              <template v-slot:popover>
                <div class="row no-gutter">
                  <div class="col-6">
                    <ReportFilterGroup label="Incoming Stock Date Range" subTitle="Date range when purchase order is expected to arrive">
                      <ReportDateRangeSelect 
                        :startDate="editingContainerPurchaseFilter.incomingStockStartDate"
                        :endDate="editingContainerPurchaseFilter.incomingStockEndDate"
                        @updateStartDate="editingContainerPurchaseFilter.incomingStockStartDate = $event"
                        @updateEndDate="editingContainerPurchaseFilter.incomingStockEndDate = $event"
                      />
                    </ReportFilterGroup>
                  </div>
                  <div class="col-6">
                    <ReportFilterGroup label="Stock Usage Date Range" subTitle="Date range for stock usage data">
                      <ReportDateRangeSelect 
                        :startDate="editingContainerPurchaseFilter.stockUsageStartDate"
                        :endDate="editingContainerPurchaseFilter.stockUsageEndDate"
                        @updateStartDate="editingContainerPurchaseFilter.stockUsageStartDate = $event"
                        @updateEndDate="editingContainerPurchaseFilter.stockUsageEndDate = $event"
                      />
                    </ReportFilterGroup>
                  </div>
                </div>
                <FormHorizontalRule />
                <ReportFilterGroup label="Purchase Order Status Filter">
                  <ReportMultiSelect :options="PurchaseOrderStatusTypeLookup" v-model="editingContainerPurchaseFilter.poStatusList" />
                </ReportFilterGroup>
                <FormHorizontalRule />
                
                <ReportFilterGroup label="Other Filters">
                  <div class="row no-gutter mb-2">
                    <div class="col-6">
                      Minimum Days Until Purchase
                      <FormInputText type="number" v-model="editingContainerPurchaseFilter.minDaysUntilPurchase" note="Minimum number of days until a purchase is required" />
                    </div>
                    <div class="col-6">
                      Add top-up stock to containers
                      <FormSwitch booleanMode v-model="editingContainerPurchaseFilter.addTopUpStock" />
                    </div>
                  </div>
                  <div class="row no-gutter mb-2">
                    <div class="col-6">
                      Minimum Shortfall Days
                      <FormInputText type="number" v-model="editingContainerPurchaseFilter.minShortfallDays" note="Minimum number of days required with the stock below required level before flagging purchase required" />
                    </div>
                    <div class="col-6">
                      Minimum Purchase Spacing Days
                      <FormInputText type="number" v-model="editingContainerPurchaseFilter.minPurchaseSpacingDays" note="Minimum number of business days required between purchases for the same item. Decrease for more frequent purchases" />
                    </div>
                  </div>
                </ReportFilterGroup>
              </template>
              <template #actionButtons>
                <FormButton class="btn-block btn-warning btn-med" @click="resetContainerOverrides"><i class="far fa-times"></i> Reset Overrides</FormButton>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <Tooltip label="Incoming stock date range">
                    <i class="far fa-arrow-alt-to-bottom mr-2"></i>{{appliedContainerPurchaseFilter.incomingStockStartDate | formatDateCustom('MMM YYYY')}} - {{appliedContainerPurchaseFilter.incomingStockEndDate | formatDateCustom('MMM YYYY')}}
                  </Tooltip>
                </div>
                <div>
                  <Tooltip label="Stock usage date range">
                    <i class="far fa-arrow-alt-from-bottom mr-2"></i>{{appliedContainerPurchaseFilter.stockUsageStartDate | formatDateCustom('MMM YYYY')}} - {{appliedContainerPurchaseFilter.stockUsageEndDate | formatDateCustom('MMM YYYY')}}
                  </Tooltip>
                </div>
                <div>
                  <i class="far fa-pencil mr-2"></i>{{containerPurchaseOverrideList.length}} Override{{displayCountPlural(containerPurchaseOverrideList.length)}}
                </div>
              </template>
            </ReportFilterPopover>

            <ContentTitle>Container Purchasing List</ContentTitle>
            <ContentSubTitle>Displays list of containers requiring purchasing</ContentSubTitle>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12">
            <Spinner v-if="loadingContainerPurchaseReport" />
            <PurchaseOrderContainerPurchaseList
              v-if="!loadingContainerPurchaseReport"
              :containerPurchasingList="containerPurchasingList"
              :usageStartDate="appliedContainerPurchaseFilter.stockUsageStartDate"
              :usageEndDate="appliedContainerPurchaseFilter.stockUsageEndDate"
              :incomingStartDate="appliedContainerPurchaseFilter.incomingStockStartDate"
              :incomingEndDate="appliedContainerPurchaseFilter.incomingStockEndDate"
              :containerOverrideList="containerPurchaseOverrideList"
              @updateOverrides="containerPurchaseOverrideList = $event; reloadContainerPurchaseReport()"
            />

            <p v-if="containerPurchasingList.length == 0 && !loadingContainerPurchaseReport" class="text-center text-muted">No containers currently require purchasing</p>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-report-supplier-performance">
        <div class="row no-gutter">
          <div class="col-lg-12">
            
            <DataTable ref="supplierPerformanceTable" :columns="supplierColumnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="4" :url="supplierReportDataURL" :hasDateRangeFilter="true" :displayExportTools="true" exportTitle="Supplier Performance Report" :displayTools="true">
              <template v-slot:title>
                <ContentTitle>Supplier Performance</ContentTitle>
                <ContentSubTitle>Showing supplier performance of all completed bulk purchase orders</ContentSubTitle>
              </template>
              <template v-slot:row="row">
                <tr :key="row.data.id">
                  <td class="table-data-nav-link">
                    <LinkPurchaseOrder :id="row.data.id" :name="row.data.sequenceNumber" />
                  </td>
                  <td>
                    <LinkContact :id="row.data.contactId" :name="row.data.supplierName" />
                  </td>
                  <td>
                    {{row.data.supplierDeliveryLeadDays || 0}}
                  </td>
                  <td>
                    {{formatDate(row.data.dateRequired)}}
                  </td>
                  <td>
                    {{formatDate(row.data.dateReceivedClosed)}}
                  </td>
                  <td>
                    <span :class="{'stats-good': row.data.daysToRequired <= 0, 'stats-bad': row.data.daysToRequired > 0}">
                      {{row.data.daysToRequired || 0}}
                    </span>
                  </td>
                </tr>
              </template>
              <template v-slot:summary="summary">
                <tr>
                  <td class="font-weight-bold">Averages</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{{summary.data.avgDaysToRequired}}</td>
                </tr>
              </template>
            </DataTable>
          </div>
        </div>
      </TabPage>

    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import StaticDataTable from '@/components/table/staticDataTable'
import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportMultiSelect from '@/components/reporting/reportMultiSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import Popover from '@/components/popover'

import PurchaseOrderReorderTable from '@/modules/purchasing/components/purchaseOrderReorderTable'
import PurchaseOrderContainerPurchaseList from '@/modules/purchasing/components/purchaseOrderContainerPurchaseList'

import { PurchaseOrderStatusTypes, PurchaseOrderStatusTypeLookup, PurchaseOrderTypeLookup } from '@/store/types'

export default {
  name: 'StockPurchasingReport',
  components: {
    StaticDataTable,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportMultiSelect,
    ReportFilterGroup,
    Popover,
    PurchaseOrderReorderTable,
    PurchaseOrderContainerPurchaseList
  },
  created() {
    this.PurchaseOrderStatusTypeLookup = PurchaseOrderStatusTypeLookup
    this.PurchaseOrderTypeLookup = PurchaseOrderTypeLookup
  },
  data() {
    return {
      defaultStockReorderFilter: {
        incomingStockStartDate: moment().local().startOf('month').startOf('day').toDate().toISOString(),
        incomingStockEndDate: moment().local().startOf('month').add(5, 'months').endOf('month').endOf('day').toDate().toISOString(),
        stockUsageStartDate: moment().local().subtract(11, 'months').startOf('day').toDate().toISOString(),
        stockUsageEndDate: moment().local().endOf('day').toDate().toISOString(),
        poStatusList: [
          PurchaseOrderStatusTypes.pendingConfirmation,
          PurchaseOrderStatusTypes.pendingShipping,
          PurchaseOrderStatusTypes.pendingReceival,
          PurchaseOrderStatusTypes.receiving
        ],
        hidePurchasingNotRequired: false,
        hidePurchasingPaused: false
      },
      editingStockReorderFilter: {},
      appliedStockReorderFilter: {},

      defaultStockPurchaseFilter: {
        incomingStockStartDate: moment().local().startOf('month').startOf('day').toDate().toISOString(),
        incomingStockEndDate: moment().local().startOf('month').add(6, 'months').endOf('month').endOf('day').toDate().toISOString(),
        stockUsageStartDate: moment().local().subtract(11, 'months').startOf('day').toDate().toISOString(),
        stockUsageEndDate: moment().local().endOf('day').toDate().toISOString(),
        poStatusList: [
          PurchaseOrderStatusTypes.pendingConfirmation,
          PurchaseOrderStatusTypes.pendingShipping,
          PurchaseOrderStatusTypes.pendingReceival,
          PurchaseOrderStatusTypes.receiving
        ],
        hideUnusedStock: true,
        hideSufficientStock: false,
        hidePurchasingNotRequired: true,
        minDaysUntilPurchase: 30,
        minShortfallDays: 5,
        minPurchaseSpacingDays: 14,
        addTopUpStock: true
      },
      editingStockPurchaseFilter: {},
      appliedStockPurchaseFilter: {},

      defaultContainerPurchaseFilter: {
        incomingStockStartDate: moment().local().startOf('month').startOf('day').toDate().toISOString(),
        incomingStockEndDate: moment().local().startOf('month').add(6, 'months').endOf('month').endOf('day').toDate().toISOString(),
        stockUsageStartDate: moment().local().subtract(11, 'months').startOf('day').toDate().toISOString(),
        stockUsageEndDate: moment().local().endOf('day').toDate().toISOString(),
        poStatusList: [
          PurchaseOrderStatusTypes.pendingConfirmation,
          PurchaseOrderStatusTypes.pendingShipping,
          PurchaseOrderStatusTypes.pendingReceival,
          PurchaseOrderStatusTypes.receiving
        ],
        minDaysUntilPurchase: 30,
        minShortfallDays: 5,
        minPurchaseSpacingDays: 14,
        addTopUpStock: true
      },
      editingContainerPurchaseFilter: {},
      appliedContainerPurchaseFilter: {},

      loadingPurchaseReport: false,
      stockPurchasingList: [],

      loadingContainerPurchaseReport: false,
      containerPurchasingList: [],
      containerPurchaseOverrideList: [],
    }
  },
  computed: {
    reorderReportDataURL() {
      if(_.isEmpty(this.appliedStockReorderFilter))
        return ''

      var params = {
        incomingStockStartDate: this.appliedStockReorderFilter.incomingStockStartDate,
        incomingStockEndDate: this.appliedStockReorderFilter.incomingStockEndDate,
        stockUsageStartDate: this.appliedStockReorderFilter.stockUsageStartDate,
        stockUsageEndDate: this.appliedStockReorderFilter.stockUsageEndDate,
        poStatusList: this.appliedStockReorderFilter.poStatusList,
        hidePurchasingNotRequired: this.appliedStockReorderFilter.hidePurchasingNotRequired,
        hidePurchasingPaused: this.appliedStockReorderFilter.hidePurchasingPaused
      }

      // Select an empty status if none set to filter out all POs if no status types selected
      if(params.poStatusList.length == 0)
        params.poStatusList = ['']

      return 'intelligence/purchasing/stockusage/report?' + $.param(params)
    },
    incomingStockMonthList() {
      if(_.isEmpty(this.appliedStockReorderFilter))
        return []

      let incomingStartDate = moment(this.appliedStockReorderFilter.incomingStockStartDate).local().startOf('month')
      let incomingEndDate = moment(this.appliedStockReorderFilter.incomingStockEndDate).local().endOf('month')
      let usageStartDate = moment(this.appliedStockReorderFilter.stockUsageStartDate).local().startOf('month')
      let usageEndDate = moment(this.appliedStockReorderFilter.stockUsageEndDate).local().endOf('month')

      let incomingStockMonthCount = Math.abs(parseInt(Math.round(moment.duration(incomingEndDate.diff(incomingStartDate)).asMonths())))
      let stockUsageMonthCount = Math.abs(parseInt(Math.round(moment.duration(moment(usageEndDate).diff(usageStartDate)).asMonths())))

      // Don't create too many columns
      // Server will also report errors with 12+ month ranges
      if(incomingStockMonthCount > 12 || stockUsageMonthCount > 12)
        return []

      var outputMonthList = []
      for(var monthIndex=0; monthIndex<incomingStockMonthCount; monthIndex++) {
        let monthField = `monthIncoming${monthIndex}`
        let monthName = moment(this.appliedStockReorderFilter.incomingStockStartDate).local().startOf('month').add(monthIndex, 'months').format('MMM')

        outputMonthList.push({ title: monthName, data: monthField })
      }

      return outputMonthList
    },
    columnList() {
      var list = [
        { title: "Stock", data: "stockName" },
        { title: "Size", data: "outputSize" },
        { title: "Required", data: "isPurchasingRequired" },
        { title: "Paused", data: "isPurchasingPaused" },
        { title: "In Stock", data: "onHandTotalSheets" },
        { title: 'Stock Processed', data: 'stockUsageTotalSheets' },
        { title: 'Breakage & Handling', data: 'stockManualTotalSheets' },
        { title: 'Monthly Avg Usage', data: 'averageStockUsage' },
      ]

      this.incomingStockMonthList.forEach(monthInfo => {
        list.push(monthInfo)
      })

      list = list.concat([
        { title: 'Usage Over Time', data: 'futureEstStockUsage' },
        { title: 'Surplus', data: 'surplusSheets' },
        { title: 'Sheets In Block', data: 'blockSize' },
        { title: 'Surplus Blocks', data: 'surplusBlocks' }
      ])

      return list
    },
    supplierReportDataURL() {
      var params = {
        includesummary: true
      }
      return 'intelligence/purchasing/supplier/performance?' + $.param(params)
    },
    supplierColumnList() {
      return [
        { title: 'Purchase Order', data: 'sequenceNumber' },
        { title: 'Supplier', data: 'supplierName' },
        { title: 'Supplier Lead Time', data: 'supplierDeliveryLeadDays' },
        { title: 'Required', data: 'dateRequired', dateFilter: true, type: 'date' },
        { title: 'Received', data: 'dateReceivedClosed', dateFilter: true, type: 'date' },
        { title: 'Days Behind', data: 'daysToRequired' }
      ]
    }
  },
  watch: {
  },
  mounted() {
    this.editingStockReorderFilter = _.clone(this.defaultStockReorderFilter)
    this.appliedStockReorderFilter = _.clone(this.defaultStockReorderFilter)
    this.reloadReorderReport()

    this.editingStockPurchaseFilter = _.clone(this.defaultStockPurchaseFilter)
    this.appliedStockPurchaseFilter = _.clone(this.defaultStockPurchaseFilter)
    this.reloadPurchaseReport()

    this.editingContainerPurchaseFilter = _.clone(this.defaultContainerPurchaseFilter)
    this.appliedContainerPurchaseFilter = _.clone(this.defaultContainerPurchaseFilter)
    this.reloadContainerPurchaseReport()
  },
  methods: {
    reload() {
      if(this.$refs.stockReorderTable)
        this.$refs.stockReorderTable.reload()
    },
    reloadReorderReport() {
      if(this.$refs.stockReorderTable)
        this.$refs.stockReorderTable.reload()
    },
    applyStockReorderFilter() {
      this.appliedStockReorderFilter = _.clone(this.editingStockReorderFilter)

      this.reloadReorderReport()
    },
    resetStockReorderFilter() {
      this.editingStockReorderFilter = _.clone(this.defaultStockReorderFilter)
      this.appliedStockReorderFilter = _.clone(this.defaultStockReorderFilter)

      this.reloadReorderReport()
    },

    reloadPurchaseReport() {
      var params = {
        incomingStockStartDate: this.appliedStockPurchaseFilter.incomingStockStartDate,
        incomingStockEndDate: this.appliedStockPurchaseFilter.incomingStockEndDate,
        stockUsageStartDate: this.appliedStockPurchaseFilter.stockUsageStartDate,
        stockUsageEndDate: this.appliedStockPurchaseFilter.stockUsageEndDate,
        poStatusList: this.appliedStockPurchaseFilter.poStatusList,
        hideUnusedStock: this.appliedStockPurchaseFilter.hideUnusedStock,
        hideSufficientStock: this.appliedStockPurchaseFilter.hideSufficientStock,
        hidePurchasingNotRequired: this.appliedStockPurchaseFilter.hidePurchasingNotRequired,
        minDaysUntilPurchase: this.appliedStockPurchaseFilter.minDaysUntilPurchase,
        minShortfallDays: this.appliedStockPurchaseFilter.minShortfallDays,
        minPurchaseSpacingDays: this.appliedStockPurchaseFilter.minPurchaseSpacingDays,
        addTopUpStock: this.appliedStockPurchaseFilter.addTopUpStock
      }

      // Select an empty status if none set to filter out all POs if no status types selected
      if(params.poStatusList.length == 0)
        params.poStatusList = ['']

      this.loadingPurchaseReport = true
      this.$apiService.analyticsService.getPurchasingReorderReport(params)
      .then(response => {
        this.stockPurchasingList = response.data
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error loading purchasing report')
      })
      .finally(() => {
        this.loadingPurchaseReport = false
      })
    },
    applyStockPurchaseFilter() {
      this.appliedStockPurchaseFilter = _.clone(this.editingStockPurchaseFilter)

      this.reloadPurchaseReport()
    },
    resetStockPurchaseFilter() {
      this.editingStockPurchaseFilter = _.clone(this.defaultStockPurchaseFilter)
      this.appliedStockPurchaseFilter = _.clone(this.defaultStockPurchaseFilter)

      this.reloadPurchaseReport()
    },

    reloadContainerPurchaseReport() {
      var containerData = {
        incomingStockStartDate: this.appliedContainerPurchaseFilter.incomingStockStartDate,
        incomingStockEndDate: this.appliedContainerPurchaseFilter.incomingStockEndDate,
        stockUsageStartDate: this.appliedContainerPurchaseFilter.stockUsageStartDate,
        stockUsageEndDate: this.appliedContainerPurchaseFilter.stockUsageEndDate,
        poStatusList: this.appliedContainerPurchaseFilter.poStatusList,
        minDaysUntilPurchase: this.appliedContainerPurchaseFilter.minDaysUntilPurchase,
        minShortfallDays: this.appliedContainerPurchaseFilter.minShortfallDays,
        minPurchaseSpacingDays: this.appliedContainerPurchaseFilter.minPurchaseSpacingDays,
        addTopUpStock: this.appliedContainerPurchaseFilter.addTopUpStock
      }

      // Select an empty status if none set to filter out all POs if no status types selected
      if(containerData.poStatusList.length == 0)
        containerData.poStatusList = ['']

      // Overrides
      if(this.containerPurchaseOverrideList.length > 0) {
        containerData.overrideList = this.containerPurchaseOverrideList
      }

      this.loadingContainerPurchaseReport = true
      this.$apiService.analyticsService.calculatePurchasingContainerReorderList(containerData)
      .then(response => {
        this.containerPurchasingList = response.data

        // Sort by containers required now vs future
        this.containerPurchasingList.sort((a, b) => {
          return moment(a.requiredPOCreationDate).diff(moment(b.requiredPOCreationDate))
        })
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error loading purchasing report')
      })
      .finally(() => {
        this.loadingContainerPurchaseReport = false
      })
    },
    applyContainerPurchaseFilter() {
      this.appliedContainerPurchaseFilter = _.clone(this.editingContainerPurchaseFilter)

      this.reloadContainerPurchaseReport()
    },
    resetContainerPurchaseFilter() {
      this.editingContainerPurchaseFilter = _.clone(this.defaultContainerPurchaseFilter)
      this.appliedContainerPurchaseFilter = _.clone(this.defaultContainerPurchaseFilter)

      this.reloadContainerPurchaseReport()
    },

    resetContainerOverrides() {
      this.containerPurchaseOverrideList = []

      this.reloadContainerPurchaseReport()
    }
    
  }
  
}
</script>